import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldWarning, Warning, Trash, Database, Stack, UsersThree, IdentificationCard,
  Buildings, Target, ClockCounterClockwise, X,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtCurrency, fmtNumber, fmtDate } from "../lib/format";

const TABS = [
  { key: "sales", label: "Sales Data", icon: Database },
  { key: "batches", label: "Upload Batches", icon: Stack },
  { key: "users", label: "Users", icon: UsersThree },
  { key: "staff", label: "Staff Master", icon: IdentificationCard },
  { key: "floors", label: "Floors", icon: Buildings },
  { key: "targets", label: "Targets", icon: Target },
  { key: "audit", label: "Audit Log", icon: ClockCounterClockwise },
];

function ConfirmDialog({ open, onClose, onConfirm, title, danger, children, confirmLabel = "Confirm", requireType, busy }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open]);
  if (!open) return null;
  const blocked = requireType && typed !== "DELETE";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="confirm-dialog">
        <div className={`flex items-center gap-2 rounded-t-lg px-5 py-3 ${danger ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-800"}`}>
          {danger && <Warning size={20} weight="fill" />}
          <h3 className="font-display text-base font-bold">{title}</h3>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm text-slate-600">
          {children}
          {requireType && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Type <span className="font-mono-data text-red-600">DELETE</span> to continue</label>
              <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="confirm-type-input"
                className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono-data text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500" placeholder="DELETE" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={onConfirm} disabled={blocked || busy} data-testid="confirm-action-btn"
            className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-700 hover:bg-blue-800"}`}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">{children}</div>;
}

export default function DataManagement() {
  const [tab, setTab] = useState("sales");
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(null); // {title, body, danger, requireType, confirmLabel, action}
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => api.get("/admin/manage/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const runAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await loadSummary();
      setDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Data & System Management">
      <div className="animate-fade-in space-y-5">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="dm-warning">
          <ShieldWarning size={24} weight="fill" className="mt-0.5 shrink-0 text-amber-500" />
          <div className="text-sm text-amber-800">
            <div className="font-bold">Production data maintenance — Admin only</div>
            <p className="mt-0.5 text-amber-700">These actions permanently remove data and cannot be undone. Historical Floor / Salesman / Category / Bill values are never rewritten. Every destructive action is written to the audit log.</p>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Sales Records", fmtNumber(summary.sales.records)],
              ["Upload Batches", fmtNumber(summary.batches)],
              ["Active Staff", fmtNumber(summary.users.staff_active)],
              ["Configured Targets", fmtNumber(summary.targets.configured)],
            ].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-slate-900">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1 overflow-x-auto border-b border-slate-200 thin-scroll">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dm-tab-${t.key}`}
              className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              <t.icon size={16} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        <div>
          {tab === "sales" && <SalesTab summary={summary} setDialog={setDialog} />}
          {tab === "batches" && <BatchesTab setDialog={setDialog} />}
          {tab === "users" && <UsersTab role={null} setDialog={setDialog} testid="users" />}
          {tab === "staff" && <UsersTab role="staff" setDialog={setDialog} testid="staff" />}
          {tab === "floors" && <FloorsTab setDialog={setDialog} />}
          {tab === "targets" && <TargetsTab setDialog={setDialog} />}
          {tab === "audit" && <AuditTab />}
        </div>
      </div>

      <ConfirmDialog
        open={!!dialog}
        onClose={() => !busy && setDialog(null)}
        onConfirm={() => runAction(dialog.action)}
        title={dialog?.title || ""}
        danger={dialog?.danger}
        requireType={dialog?.requireType}
        confirmLabel={dialog?.confirmLabel}
        busy={busy}
      >
        {dialog?.body}
      </ConfirmDialog>
    </Layout>
  );
}

/* ---------------- Sales ---------------- */
function SalesTab({ summary, setDialog }) {
  const [txns, setTxns] = useState([]);
  const [sel, setSel] = useState({});
  const load = useCallback(() => api.get("/admin/transactions", { params: { limit: 200 } }).then((r) => { setTxns(r.data); setSel({}); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const deleteAll = () => setDialog({
    title: "Delete ALL sales", danger: true, requireType: true, confirmLabel: "Delete All Sales",
    body: (<div><p>You are about to permanently delete <b>all {fmtNumber(summary?.sales.records || 0)} sales records</b>{summary?.sales.start && <> ({fmtDate(summary.sales.start)} – {fmtDate(summary.sales.end)})</>}.</p><p className="mt-2 font-semibold text-red-600">This cannot be undone. Dashboards, targets and incentives will recalculate to empty.</p></div>),
    action: async () => { const r = await api.post("/admin/manage/sales/delete", { all: true, confirm: "DELETE" }); toast.success(`Deleted ${r.data.deleted} sales records`); load(); },
  });
  const deleteSelected = () => setDialog({
    title: `Delete ${selectedIds.length} selected sale(s)?`, danger: true, requireType: selectedIds.length > 100, confirmLabel: "Delete Selected",
    body: <p>This will permanently remove <b>{selectedIds.length}</b> selected sales record(s). Affected dashboard metrics recalculate automatically.</p>,
    action: async () => { const r = await api.post("/admin/manage/sales/delete", { ids: selectedIds }); toast.success(`Deleted ${r.data.deleted} records`); load(); },
  });

  return (
    <div className="space-y-3">
      <Row>
        <div>
          <div className="font-semibold text-slate-800">Delete All Sales</div>
          <div className="text-sm text-slate-500">{fmtNumber(summary?.sales.records || 0)} records{summary?.sales.start && ` · ${fmtDate(summary.sales.start)} – ${fmtDate(summary.sales.end)}`}</div>
        </div>
        <button onClick={deleteAll} disabled={!summary?.sales.records} data-testid="delete-all-sales" className="flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"><Trash size={16} weight="bold" /> Delete All Sales</button>
      </Row>

      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">Recent transactions {txns.length ? `(latest ${txns.length})` : ""}</h4>
        <button onClick={deleteSelected} disabled={!selectedIds.length} data-testid="delete-selected-sales" className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">Delete Selected ({selectedIds.length})</button>
      </div>
      {txns.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="sales-empty">No sales data available.</div>
      ) : (
        <div className="thin-scroll max-h-[420px] overflow-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left"><input type="checkbox" data-testid="sales-select-all" onChange={(e) => setSel(e.target.checked ? Object.fromEntries(txns.map((t) => [t.record_id, true])) : {})} checked={txns.length > 0 && selectedIds.length === txns.length} /></th>
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">POS Bill</th><th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Floor</th><th className="px-3 py-2 text-left">Salesman</th><th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.record_id} className="border-t border-slate-100">
                  <td className="px-3 py-2"><input type="checkbox" checked={!!sel[t.record_id]} onChange={(e) => setSel((s) => ({ ...s, [t.record_id]: e.target.checked }))} /></td>
                  <td className="px-3 py-2">{fmtDate(t.business_date)}</td>
                  <td className="px-3 py-2 font-mono-data">{t.pos_bill_no === null ? "0 (return)" : t.pos_bill_no}</td>
                  <td className="px-3 py-2">{t.type === "RETURN" ? <span className="text-red-600">RETURN</span> : "SALE"}</td>
                  <td className="px-3 py-2">{t.floor}</td><td className="px-3 py-2">{t.salesman}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{fmtCurrency(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Batches ---------------- */
function BatchesTab({ setDialog }) {
  const [batches, setBatches] = useState([]);
  const [sel, setSel] = useState({});
  const load = useCallback(() => api.get("/uploads").then((r) => { setBatches(r.data); setSel({}); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const selectedUploadIds = batches.filter((b) => sel[b.upload_id]).map((b) => b.upload_id);

  const deleteSelected = () => setDialog({
    title: `Delete ${selectedUploadIds.length} upload batch(es)?`, danger: true, requireType: selectedUploadIds.length > 5, confirmLabel: "Delete Selected",
    body: <p>This removes the selected upload batch(es) and only the transaction records imported by them. Unrelated data is preserved and metrics recalculate automatically.</p>,
    action: async () => { const r = await api.post("/admin/manage/batches/delete", { upload_ids: selectedUploadIds }); toast.success(`Deleted ${r.data.batches_deleted} batch(es), ${r.data.records_deleted} records`); load(); },
  });
  const deleteAll = () => setDialog({
    title: "Delete ALL upload batches", danger: true, requireType: true, confirmLabel: "Delete All Batches",
    body: <p>Permanently delete <b>all {fmtNumber(batches.length)} upload batches</b> and every transaction imported through them.</p>,
    action: async () => { const r = await api.post("/admin/manage/batches/delete", { all: true, confirm: "DELETE" }); toast.success(`Deleted ${r.data.batches_deleted} batches, ${r.data.records_deleted} records`); load(); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">Upload History</h4>
        <div className="flex gap-2">
          <button onClick={deleteSelected} disabled={!selectedUploadIds.length} data-testid="delete-selected-batches" className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">Delete Selected ({selectedUploadIds.length})</button>
          <button onClick={deleteAll} disabled={!batches.length} data-testid="delete-all-batches" className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">Delete All</button>
        </div>
      </div>
      {batches.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="batches-empty">No upload batches available.</div>
      ) : (
        <div className="thin-scroll overflow-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2"><input type="checkbox" data-testid="batches-select-all" onChange={(e) => setSel(e.target.checked ? Object.fromEntries(batches.map((b) => [b.upload_id, true])) : {})} checked={batches.length > 0 && selectedUploadIds.length === batches.length} /></th>
                <th className="px-3 py-2 text-left">File</th><th className="px-3 py-2 text-left">Bill Dates</th><th className="px-3 py-2 text-right">Records</th><th className="px-3 py-2 text-left">By</th><th className="px-3 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.upload_id} className="border-t border-slate-100">
                  <td className="px-3 py-2"><input type="checkbox" checked={!!sel[b.upload_id]} onChange={(e) => setSel((s) => ({ ...s, [b.upload_id]: e.target.checked }))} /></td>
                  <td className="px-3 py-2 font-medium text-slate-800">{b.file_name}</td>
                  <td className="px-3 py-2">{(b.business_dates || []).map(fmtDate).join(", ")}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{fmtNumber(b.row_count)}</td>
                  <td className="px-3 py-2">{b.uploaded_by}</td>
                  <td className="px-3 py-2"><span className={b.status === "completed" ? "text-emerald-600" : "text-slate-400"}>{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Users / Staff ---------------- */
function UsersTab({ role, setDialog, testid }) {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState({});
  const load = useCallback(() => {
    const url = role === "staff" ? "/staff" : "/users";
    api.get(url).then((r) => { setUsers(r.data); setSel({}); }).catch(() => {});
  }, [role]);
  useEffect(() => { load(); }, [load]);
  const eligible = users.filter((u) => u.role !== "admin");
  const selectedIds = eligible.filter((u) => sel[u.id]).map((u) => u.id);

  const deactivateSelected = () => setDialog({
    title: `Deactivate ${selectedIds.length} user(s)?`, danger: true, confirmLabel: "Deactivate Selected",
    body: <p>Selected users will no longer be able to log in. <b>Historical sales and incentive records are preserved.</b> Admin accounts are never affected.</p>,
    action: async () => { const r = await api.post("/admin/manage/users/deactivate", { ids: selectedIds }); toast.success(`Deactivated ${r.data.deactivated} user(s)`); load(); },
  });
  const deactivateAll = () => setDialog({
    title: role === "staff" ? "Deactivate ALL eligible staff" : "Deactivate ALL eligible users", danger: true, requireType: true, confirmLabel: "Deactivate All",
    body: <p>All active {role === "staff" ? "staff" : "non-admin"} users will be deactivated (soft). <b>Admin accounts are excluded and protected.</b> Historical records are preserved.</p>,
    action: async () => { const r = await api.post("/admin/manage/users/deactivate", { all_eligible: true, roles: role === "staff" ? ["staff"] : null, confirm: "DELETE" }); toast.success(`Deactivated ${r.data.deactivated} user(s)`); load(); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">{role === "staff" ? "Staff Master" : "All Users"}</h4>
        <div className="flex gap-2">
          <button onClick={deactivateSelected} disabled={!selectedIds.length} data-testid={`deactivate-selected-${testid}`} className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">Deactivate Selected ({selectedIds.length})</button>
          <button onClick={deactivateAll} disabled={!eligible.some((u) => u.active)} data-testid={`deactivate-all-${testid}`} className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">Deactivate All Eligible</button>
        </div>
      </div>
      {users.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>No {role === "staff" ? "staff" : "users"} available.</div>
      ) : (
        <div className="thin-scroll overflow-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2"></th><th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-left">Core Floor</th><th className="px-3 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isAdmin = u.role === "admin";
                return (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">{isAdmin ? <span className="text-[10px] font-bold uppercase text-amber-500" title="Protected admin">🔒</span> : <input type="checkbox" checked={!!sel[u.id]} onChange={(e) => setSel((s) => ({ ...s, [u.id]: e.target.checked }))} data-testid={`select-${testid}-${u.username}`} />}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{u.name} <span className="text-xs text-slate-400">@{u.username}</span></td>
                    <td className="px-3 py-2 capitalize">{u.role}{isAdmin && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">PROTECTED</span>}</td>
                    <td className="px-3 py-2">{u.core_floor || "—"}</td>
                    <td className="px-3 py-2">{u.active ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Disabled</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Floors ---------------- */
function FloorsTab({ setDialog }) {
  const [floors, setFloors] = useState([]);
  const [sel, setSel] = useState({});
  const load = useCallback(() => api.get("/admin/manage/floors").then((r) => { setFloors(r.data); setSel({}); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const selected = floors.filter((f) => sel[f.floor_name]).map((f) => f.floor_name);
  const deps = (f) => `${f.core_floor_assignments} core-floor assignment(s), ${fmtNumber(f.historical_txn_count)} historical txns`;

  const deactivate = () => setDialog({
    title: `Deactivate ${selected.length} floor(s)?`, danger: false, confirmLabel: "Deactivate",
    body: (<div><p>These floor targets will be marked inactive. <b>Historical sales are never deleted or rewritten.</b></p><ul className="mt-2 list-disc pl-5 text-xs">{floors.filter((f) => sel[f.floor_name]).map((f) => <li key={f.floor_name}>{f.floor_name} — used by {deps(f)}</li>)}</ul></div>),
    action: async () => { const r = await api.post("/admin/manage/floors/deactivate", { floors: selected }); toast.success(`Deactivated ${r.data.deactivated} floor(s)`); load(); },
  });
  const del = () => setDialog({
    title: `Delete ${selected.length} floor config(s)?`, danger: true, requireType: true, confirmLabel: "Delete Floor Config",
    body: (<div><p className="font-semibold text-red-600">This removes the floor configuration (target). Historical sales for these floors remain intact and keep their original floor value.</p><ul className="mt-2 list-disc pl-5 text-xs">{floors.filter((f) => sel[f.floor_name]).map((f) => <li key={f.floor_name}>{f.floor_name} — dependencies: {deps(f)}</li>)}</ul></div>),
    action: async () => { const r = await api.post("/admin/manage/floors/delete", { floors: selected, confirm: "DELETE" }); toast.success(`Deleted ${r.data.deleted} floor config(s)`); load(); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">Configured Floors</h4>
        <div className="flex gap-2">
          <button onClick={deactivate} disabled={!selected.length} data-testid="deactivate-floors" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Deactivate</button>
          <button onClick={del} disabled={!selected.length} data-testid="delete-floors" className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">Delete Config</button>
        </div>
      </div>
      {floors.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="floors-empty">No configured floors available.</div>
      ) : (
        <div className="space-y-2">
          {floors.map((f) => (
            <Row key={f.floor_name}>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={!!sel[f.floor_name]} onChange={(e) => setSel((s) => ({ ...s, [f.floor_name]: e.target.checked }))} data-testid={`select-floor-${f.floor_name}`} />
                <div>
                  <div className="font-semibold text-slate-800">{f.floor_name} {f.active ? <span className="ml-1 text-xs text-emerald-600">Active</span> : <span className="ml-1 text-xs text-slate-400">Inactive</span>}</div>
                  <div className="text-xs text-slate-500">Target {fmtCurrency(f.monthly_target)} · used by {deps(f)}</div>
                </div>
              </label>
            </Row>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Targets ---------------- */
function TargetsTab({ setDialog }) {
  const [targets, setTargets] = useState([]);
  const [sel, setSel] = useState({});
  const load = useCallback(() => api.get("/settings/targets").then((r) => { setTargets(r.data); setSel({}); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const selected = targets.filter((t) => sel[t.floor_name]).map((t) => t.floor_name);

  const delSelected = () => setDialog({
    title: `Delete ${selected.length} target config(s)?`, danger: true, confirmLabel: "Delete Selected",
    body: <p>This removes the selected monthly <b>target configuration</b>. Achievement values are computed live and reset automatically. Completed-period history is preserved.</p>,
    action: async () => { const r = await api.post("/admin/manage/targets/delete", { floors: selected }); toast.success(`Deleted ${r.data.deleted} target(s)`); load(); },
  });
  const delAll = () => setDialog({
    title: "Delete ALL target configuration", danger: true, requireType: true, confirmLabel: "Delete All Targets",
    body: <p>Remove <b>all {targets.length} monthly target configurations</b>. This clears current target config only; historical completed-period configuration is preserved for incentive accuracy.</p>,
    action: async () => { const r = await api.post("/admin/manage/targets/delete", { all: true, confirm: "DELETE" }); toast.success(`Deleted ${r.data.deleted} target(s)`); load(); },
  });

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        <b>Target Configuration</b> vs <b>Achievement</b>: achievement values are always computed live from sales, so they reset automatically when sales/targets change. Only the target <i>configuration</i> is edited here.
      </div>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-700">Monthly Target Configuration</h4>
        <div className="flex gap-2">
          <button onClick={delSelected} disabled={!selected.length} data-testid="delete-selected-targets" className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">Delete Selected ({selected.length})</button>
          <button onClick={delAll} disabled={!targets.length} data-testid="delete-all-targets" className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">Delete All</button>
        </div>
      </div>
      {targets.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="targets-empty">No target configuration available.</div>
      ) : (
        <div className="space-y-2">
          {targets.map((t) => (
            <Row key={t.floor_name}>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={!!sel[t.floor_name]} onChange={(e) => setSel((s) => ({ ...s, [t.floor_name]: e.target.checked }))} data-testid={`select-target-${t.floor_name}`} />
                <div>
                  <div className="font-semibold text-slate-800">{t.floor_name}</div>
                  <div className="text-xs text-slate-500">Monthly target {fmtCurrency(t.monthly_target)} {t.active ? "" : "· inactive"}</div>
                </div>
              </label>
            </Row>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Audit ---------------- */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/manage/audit").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-bold text-slate-700">Audit Log</h4>
      {logs.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="audit-empty">No destructive actions recorded yet.</div>
      ) : (
        <div className="thin-scroll overflow-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Admin</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-right">Count</th><th className="px-3 py-2 text-left">Detail</th></tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid="audit-row">
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-3 py-2 font-medium">{l.actor}</td>
                  <td className="px-3 py-2"><span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono-data text-xs">{l.action}</span></td>
                  <td className="px-3 py-2 text-right font-mono-data">{fmtNumber(l.count)}</td>
                  <td className="px-3 py-2 text-slate-600">{l.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
